import { useCallback, useEffect, useMemo, useState } from 'react'
import { sortBy } from 'lodash-es'
import ShowChart from '@mui/icons-material/ShowChart'

import { ISelectOption, LineSegmentGenericData } from '$/types'
import { IExtendedColDef, ILineSegments } from 'app/services/api/agGrid/agGrid'
import DraggableColumn from 'common/components/DraggableColumn'
import { SelectDropdownWrapper } from 'common/components/SelectDropdownWrapper'
import { processDateYAxisOptions } from 'common/utils/chart/chartUtils'

import DataInputGraph from 'features/RiskManagement/Components/Graphs/Implementations/DataInputGraph'

import tailwind from 'styles/themeTokens'

interface ILRSChart {
  filteredLineSegments: ILineSegments[]
  ContinuousRouteList: ISelectOption[]
  defaultDataViewColDefs: IExtendedColDef[]
  selectedRoute: ISelectOption | null
  setSelectedRoute: (value: ISelectOption | null) => void
}

interface ISelectOptionWithUnitType extends ISelectOption {
  unitTypeName?: string
}

interface IColumnsMemo {
  columns: ISelectOption[]
  unitTypeNames: Record<string, string | undefined>[]
}

const DataLRSCharts = ({
  filteredLineSegments,
  ContinuousRouteList,
  defaultDataViewColDefs,
  selectedRoute,
  setSelectedRoute
}: ILRSChart) => {
  const { columns, unitTypeNames }: IColumnsMemo = useMemo(() => {
    const _ignoreColumns = [
      'Id',
      'ToMeasure',
      'FromMeasure',
      'Length',
      'LengthMiles',
      'FromDate',
      'ToDate',
      'RouteName',
      'EventTypeCode',
      'ContinousRouteId',
      'OhcaReason'
    ]

    return sortBy(
      defaultDataViewColDefs
        .filter(
          (defaultColDef) =>
            !_ignoreColumns.some(
              (columnCode) => columnCode === defaultColDef.code
            )
        )
        .map((colDef) => ({
          label: colDef.name ?? colDef.code ?? '',
          value: colDef.code ?? '',
          unitTypeName: colDef.unitTypeName
        })),
      'label'
    ).reduce(
      (accumulator: IColumnsMemo, column: ISelectOptionWithUnitType) => {
        // match the sorted index of values and unit types
        // and split them up for typing purposes
        const { unitTypeName, ...selectOption } = column

        accumulator.unitTypeNames.push({
          label: column.label,
          unit: unitTypeName
        })
        accumulator.columns.push(selectOption)

        return accumulator
      },
      {
        columns: [],
        unitTypeNames: []
      }
    )
  }, [defaultDataViewColDefs])

  const deriveYValues = (label: string) => {
    return unitTypeNames.find((unitObj) => unitObj.label === label)?.unit
  }

  const [selectedAttributes, setSelectedAttributes] = useState<ISelectOption[]>(
    []
  )

  useEffect(() => {
    const noDataSourceFilteredColumns = columns.filter(
      (column) => !column.label.endsWith('Data Source')
    )

    setSelectedAttributes(noDataSourceFilteredColumns)
  }, [columns])

  const handleSelectionChange = useCallback((selections: ISelectOption[]) => {
    setSelectedAttributes(selections)
  }, [])

  useEffect(() => {
    /* remove '...' from the 'rest' label tag
      example: +3 instead of +3... */
    const restElement = document.getElementsByClassName(
      'ant-select-selection-overflow-item-rest'
    )[0]

    if (restElement != null) {
      const spanElement = restElement.getElementsByClassName(
        'ant-select-selection-item-content'
      )[0]

      spanElement.innerHTML = spanElement.innerHTML
        .replaceAll('.', '')
        .replaceAll(' ', '')
    }
  }, [selectedAttributes])

  const isBooleanAttribute = (attribute: string) =>
    ['True', 'False'].includes(attribute)

  const obtainValuesFromAttribute = (
    segmentsSortedByFromMeasure: ILineSegments[],
    attributeCode: string
  ) => {
    let yAxisLabels = []

    const YAxisValues = segmentsSortedByFromMeasure.map(
      (lineSegment: ILineSegments) => lineSegment[attributeCode]
    )

    YAxisValues.push(YAxisValues[YAxisValues.length - 1])

    if (isBooleanAttribute(YAxisValues[0] as string)) {
      yAxisLabels = ['False', 'True']
    } else {
      yAxisLabels = [...sortBy(Array.from(new Set(YAxisValues)))]
    }

    return [yAxisLabels, YAxisValues]
  }

  const getAllXValuesFromSegments = (
    segmentsSortedByFromMeasure: ILineSegments[]
  ) => {
    const XValues: number[] = []

    if (segmentsSortedByFromMeasure.length === 0) {
      return XValues
    }

    XValues.push(segmentsSortedByFromMeasure[0].FromMeasure)
    XValues.push(segmentsSortedByFromMeasure[0].ToMeasure)

    for (let i = 1; i < segmentsSortedByFromMeasure.length; ++i) {
      XValues.push(segmentsSortedByFromMeasure[i].ToMeasure)
    }

    XValues.push(
      segmentsSortedByFromMeasure[segmentsSortedByFromMeasure.length - 1]
        ?.ToMeasure
    )

    return XValues
  }

  const dummySegmentTemplate = {
    Id: -1,
    FromMeasure: '',
    ToMeasure: '',
    data: undefined,
    ContinuousRouteId: -1,
    RouteName: '-',
    FromDate: '',
    ToDate: ''
  }

  const parseSegmentData = (currentSegments: ILineSegments[]) => {
    const tempNewCurSeg: ILineSegments[] = []

    if (currentSegments.length <= 1) {
      return currentSegments
    }

    for (let i = 0; i < currentSegments.length - 1; i++) {
      if (currentSegments[i].ToMeasure !== currentSegments[i + 1].FromMeasure) {
        tempNewCurSeg.push(currentSegments[i])

        tempNewCurSeg.push({
          ...dummySegmentTemplate,
          ContinuousRouteId: currentSegments[i].ContinuousRouteId,
          FromMeasure: currentSegments[i].ToMeasure,
          ToMeasure: currentSegments[i + 1].FromMeasure
        })
      } else {
        tempNewCurSeg.push(currentSegments[i])
      }
    }

    tempNewCurSeg.push(currentSegments[currentSegments.length - 1])

    return tempNewCurSeg
  }

  const mergeSegmentData = (
    prevSegments: ILineSegments[],
    curSegments: ILineSegments[]
  ) => {
    if (prevSegments.length == 0 && curSegments.length == 0) {
      return []
    } else if (prevSegments.length == 0) {
      return curSegments
    } else if (curSegments.length == 0) {
      return prevSegments
    }

    if (prevSegments[0].FromMeasure < curSegments[0].FromMeasure) {
      curSegments.unshift({
        ...dummySegmentTemplate,
        FromMeasure: prevSegments[0].FromMeasure,
        ToMeasure: curSegments[0].ToMeasure,
        FromDate: curSegments[0].FromDate
      })
    } else if (curSegments[0].FromMeasure < prevSegments[0].FromMeasure) {
      prevSegments.unshift({
        ...dummySegmentTemplate,
        FromMeasure: curSegments[0].FromMeasure,
        ToMeasure: prevSegments[0].ToMeasure,
        FromDate: prevSegments[0].FromDate,
        ToDate: prevSegments[0].ToDate
      })
    }

    const flattenSegments = (segmentArray: ILineSegments[]) => {
      const tempSeg = [segmentArray[0].FromMeasure]

      for (let i = 0; i < segmentArray.length; i++) {
        tempSeg.push(segmentArray[i].ToMeasure)
      }

      return tempSeg
    }

    const prevSeg = flattenSegments(prevSegments)
    const curSeg = flattenSegments(curSegments)

    const combinedSegments = Array.from(
      new Set(
        [...prevSeg, ...curSeg].sort(function (a, b) {
          return a - b
        })
      )
    )

    const mergedSegments = []

    for (let i = 0; i < combinedSegments.length - 1; ++i) {
      mergedSegments.push({
        ...dummySegmentTemplate,
        FromMeasure: combinedSegments[i],
        ToMeasure: combinedSegments[i + 1]
      })
    }

    return mergedSegments
  }

  const spliceSegmentData = (
    prevSeg: ILineSegments[],
    curSeg: ILineSegments[]
  ) => {
    const mergedSegments = mergeSegmentData(prevSeg, curSeg)

    if (mergedSegments.length == 0) {
      return [[], []]
    }

    const curTempData: ILineSegments[] = [...mergedSegments]
    const prevTempData: ILineSegments[] = [...mergedSegments]

    for (let i = 0; i < curTempData.length; i++) {
      const selectedSegment = curSeg.find(
        (seg) =>
          seg.FromMeasure <= curTempData[i].FromMeasure &&
          seg.ToMeasure >= curTempData[i].ToMeasure
      )

      if (selectedSegment) {
        curTempData[i] = {
          ...selectedSegment,
          ToMeasure: curTempData[i].ToMeasure,
          FromMeasure: curTempData[i].FromMeasure
        }
      } else {
        curTempData[i] = {
          ...dummySegmentTemplate,
          FromMeasure: curTempData[i].FromMeasure,
          ToMeasure: curTempData[i].ToMeasure
        }
      }
    }

    for (let i = 0; i < prevTempData.length; i++) {
      const selectedSegment = prevSeg.find(
        (seg: ILineSegments) =>
          seg.FromMeasure <= prevTempData[i].FromMeasure &&
          seg.ToMeasure >= prevTempData[i].ToMeasure
      )

      if (selectedSegment) {
        prevTempData[i] = {
          ...selectedSegment,
          ToMeasure: prevTempData[i].ToMeasure,
          FromMeasure: prevTempData[i].FromMeasure
        }
      } else {
        prevTempData[i] = {
          ...dummySegmentTemplate,
          FromMeasure: prevTempData[i].FromMeasure,
          ToMeasure: prevTempData[i].ToMeasure
        }
      }
    }

    return [prevTempData, curTempData]
  }

  const children = selectedAttributes.map((selection, index) => {
    const segmentsSortedByFromMeasure = sortBy(
      filteredLineSegments,
      'FromMeasure'
    )

    const currentSegments = parseSegmentData(
      segmentsSortedByFromMeasure.filter((seg) => seg.ToDate === '')
    )

    const prevSegments = parseSegmentData(
      segmentsSortedByFromMeasure.filter((seg) => seg.ToDate !== '')
    )

    const [newPrevSegments, newCurrentSegments] = spliceSegmentData(
      prevSegments,
      currentSegments
    )

    for (let i = 0; i < newPrevSegments.length; ++i) {
      const curSeg = newCurrentSegments.find(
        (seg) => seg.FromMeasure === newPrevSegments[i].FromMeasure
      )

      if (newPrevSegments[i].data === undefined) {
        if (curSeg && curSeg.data !== undefined) {
          newPrevSegments[i] = curSeg
        }
      }
    }

    let [currentYLabels, currentYValues]: LineSegmentGenericData[][] =
      currentSegments.length > 0
        ? obtainValuesFromAttribute(
            newCurrentSegments,
            selection.value as string
          )
        : [[], []]

    let [prevYLabels, prevYValues]: LineSegmentGenericData[][] =
      prevSegments.length > 0
        ? obtainValuesFromAttribute(newPrevSegments, selection.value as string)
        : [[], []]

    const isDate =
      selection.label.includes('Date') &&
      !selection.label.includes('Data Source')

    if (isDate) {
      currentYValues = currentYValues.map((segment) =>
        typeof segment == 'string' ? segment.split('T')[0] : segment
      )

      currentYLabels = currentYValues

      prevYValues = prevYValues.map((segment) =>
        typeof segment == 'string' ? segment.split('T')[0] : segment
      )

      prevYLabels = prevYValues
    }

    const xValues = getAllXValuesFromSegments(newCurrentSegments)

    if (currentYValues.length == 1) {
      currentYValues.push(currentYValues[0])
    }

    if (prevYValues.length == 1) {
      prevYValues.push(prevYValues[0])
    }

    let YAxis = decodeURIComponent(deriveYValues(selection.label) ?? '')

    if (YAxis.length > 8) {
      YAxis = YAxis.slice(0, 9) + '...'
    }

    let yAxisOptions = Array.from(new Set([...currentYLabels, ...prevYLabels]))

    if (yAxisOptions && yAxisOptions.length > 1 && isDate) {
      yAxisOptions = processDateYAxisOptions(yAxisOptions)
    }

    return (
      <div key={selection.label}>
        <DataInputGraph
          isDraggable
          showLoading={false}
          XValues={xValues}
          s1Values={prevYValues}
          s2Values={currentYValues} // Current data needs to be in s2 so it overlays the previous
          s1Label={selection.label || ''}
          s2Label='' // Required left blank to show nothing
          YLabel={YAxis ?? ''}
          XLabel='Distance (ft)'
          graphIndexNumber={index}
          attributeName={selection.label}
          selectedAttributes={selectedRoute ? selectedAttributes : []}
          lineColor1={tailwind.theme.colors.blue.lighthouse[200]} // Color requested by Darcy
          lineColor2={tailwind.theme.colors.lighthouseBlue.DEFAULT}
          isContinuous
          yAxisOptions={yAxisOptions}
          dataView
        />
      </div>
    )
  })

  return (
    <div className='p-3'>
      <div className='items-center font-semibold'>
        <ShowChart />
        <span className='ml-2'>Chart Preview</span>
      </div>
      <div className='my-3'>
        <SelectDropdownWrapper
          className='mb-1 w-full'
          options={sortBy(ContinuousRouteList, [
            (i) => i.label.toLowerCase(),
            (i) => i.label
          ]).map((option) => ({
            ...option,
            value: String(option.value)
          }))}
          value={
            selectedRoute == null ? undefined : String(selectedRoute.value)
          }
          onChange={(_, selection) => {
            if (!Array.isArray(selection)) {
              setSelectedRoute(selection as ISelectOption)
            }
          }}
          placeholder='Select Route'
        />
        <SelectDropdownWrapper
          className='w-full'
          mode='tags'
          options={columns.map((option) => ({
            ...option,
            value: String(option.value)
          }))}
          value={selectedAttributes.map(({ value }) => String(value))}
          onChange={(_, selections) => {
            handleSelectionChange(selections as ISelectOption[])
          }}
          placeholder='Select Attributes'
        />
      </div>
      <DraggableColumn>{children}</DraggableColumn>
    </div>
  )
}

export default DataLRSCharts
